import { useCallback, useContext, useState } from 'react';
import { useStatuses } from '@namespace/account';
import { LOG_ACTIONS, useLogAmplitude } from '@namespace/analytics';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { FORM_FACTORS_MAP, useDeviceStore } from '@namespace/device';
import { LOCAL_STORAGE_KEYS, setLocalStorageValue } from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { useHistory } from '@namespace/router';
import { UserContext } from '@namespace/user';
import { accept } from '../../api/acceptBonus';
import { activateBsBonus } from '../../api/activateBsBonus';
import { acceptWager } from '../../api/fundsBonuses';
import { selectBonusSystemBonus } from '../../api/selectBonusSystemBonus';
import { unselectBonusSystemBonus } from '../../api/unselectBonusSystemBonus';
import { bonusTab } from '../../constants';
import { useActiveBsDepositBonusId } from '../../store';
import getOutputBetType from '../../utils/getOutputBetType';

export const useFundsBonusActions = (bonus, onAccept, onWagerAccept) => {
  const [isRequestProcessing, setIsRequestProcessing] = useState(false);
  const { t, f, language, timezone } = useI18n();
  const { isBonusSliderCRMBonusClickable = true } = useOtherSettings();
  const activeBonusSystemBonusId = useActiveBsDepositBonusId();
  const { push } = useHistory();
  const {
    liveSportAll,
    casinoWager,
    deposit,
    games,
    createWallet: createWalletPage,
    fundsBonusDetails,
    profilePage
  } = useGeneralRoutes();
  const { isNotVerified, isNeedSetupLimits } = useStatuses();
  const {
    isBsBonus,
    isNoWallet,
    isShowWagerButton,
    isPotential,
    isBonusSystemPotential,
    isWagerAvailable,
    isCanAccept,
    isBonusSystemProposed,
    bonusId,
    bonusModelId,
    currency,
    outputBetType,
    services,
    outputMaxTotal,
    outputBetTotal,
    isCashback
  } = bonus;
  const casinoWagerLink = casinoWager || games;

  const { isDesktop } = useDeviceStore();
  const platform = isDesktop
    ? FORM_FACTORS_MAP.DESKTOP
    : FORM_FACTORS_MAP.MOBILE;

  const DATE_FORMATS_TYPES = f.getFormats();
  const [user] = useContext(UserContext);
  const { registrationDate } = user;
  const logAmplitude = useLogAmplitude();

  const { errorNotification, successNotification } = useNotifications();

  const handleAccept = useCallback(async () => {
    try {
      setIsRequestProcessing(true);
      const message = await accept(
        {
          bonus_id: bonusId,
          lang: language,
          time_zone: timezone,
          platform
        },
        bonusTab.bonus
      );
      setIsRequestProcessing(false);
      successNotification(t(message));
      if (onAccept) {
        onAccept();
      }
      logAmplitude({
        actionName: LOG_ACTIONS.BONUS_ACCEPT_CLICK,
        attributes: {
          bonus_option: 'funds',
          bonus_template_id: bonusModelId,
          bonus_source: isBsBonus ? 'bonus_system' : 'crm',
          ...(!isCashback && { amount_for_wager: Number(outputMaxTotal) }),
          registration_date: f
            .getDateTime({ date: registrationDate?.replace('Z', '') })
            .toFormat(DATE_FORMATS_TYPES.API_WITH_TIME)
        }
      });
    } catch (e) {
      setIsRequestProcessing(false);
      errorNotification(
        t(
          e.message ||
            (e.errorCode && `accountingMessages.${e.errorCode}`) ||
            'accountingMessages.accounting_error_6',
          [currency]
        )
      );
    }
  }, [
    bonusId,
    language,
    timezone,
    platform,
    successNotification,
    t,
    onAccept,
    errorNotification,
    currency
  ]);

  const handleWagerAccept = useCallback(async () => {
    try {
      setIsRequestProcessing(true);
      const message = await acceptWager(bonusId, language, platform);
      setIsRequestProcessing(false);
      successNotification(t(message));
      if (onWagerAccept) {
        onWagerAccept();
      }
    } catch (e) {
      setIsRequestProcessing(false);
      errorNotification(
        e.message ? t(e.message) : t('accountingMessages.accounting_error_6')
      );
    }
  }, [
    bonusId,
    language,
    platform,
    successNotification,
    t,
    onWagerAccept,
    errorNotification
  ]);

  const handleStartPrewagerBonus = useCallback(async () => {
    try {
      setIsRequestProcessing(true);
      await activateBsBonus({ bonusId: bonusModelId });
      setIsRequestProcessing(false);
      successNotification(t('bonus.notification.startwagering'));
      if (onAccept) {
        onAccept();
      }
    } catch (e) {
      errorNotification(t(`prewagerMessages.${e}`));
    }
  }, [bonusModelId, errorNotification, onAccept, successNotification, t]);

  const wagerType = getOutputBetType(outputBetType) || services;
  const isWagerAcceptCasino = wagerType?.toLowerCase()?.includes('casino');

  const acceptBonus = useCallback(
    async (e) => {
      e.stopPropagation();
      if (isNoWallet) {
        push(createWalletPage);
      } else if (isShowWagerButton) {
        logAmplitude({
          actionName: LOG_ACTIONS.BONUS_WAGER_CLICK,
          attributes: {
            bonus_option: 'funds',
            bonus_template_id: bonusModelId,
            bonus_source: isBsBonus ? 'bonus_system' : 'crm',
            ...(!isCashback && { amount_for_wager: Number(outputMaxTotal) }),
            ...(!isCashback && {
              wagered_percent:
                Number(outputMaxTotal) === 0
                  ? 0
                  : Math.floor((outputBetTotal / Number(outputMaxTotal)) * 100)
            }),
            registration_date: f
              .getDateTime({ date: registrationDate?.replace('Z', '') })
              .toFormat(DATE_FORMATS_TYPES.API_WITH_TIME)
          }
        });
        push(isWagerAcceptCasino ? casinoWagerLink : liveSportAll);
      } else if (isPotential) {
        if (isBonusSliderCRMBonusClickable) {
          if (activeBonusSystemBonusId) {
            await unselectBonusSystemBonus({
              bonusId: activeBonusSystemBonusId
            });
          }
          setLocalStorageValue(
            LOCAL_STORAGE_KEYS.BONUS_SLIDER_ACTIVE_BONUS,
            bonusId
          );
        }
        logAmplitude({
          actionName: LOG_ACTIONS.DEPOSIT_BUTTON_CLICK,
          attributes: {
            entry_point: 'bonuses',
            verified: !isNotVerified,
            deposit_limits_set: !isNeedSetupLimits,
            bonus_option: 'funds',
            bonus_template_id: bonusModelId
          },
          isMatchPath: true,
          additionalRoutes: {
            profile: profilePage
          }
        });
        push(deposit);
      } else if (isBonusSystemPotential) {
        await selectBonusSystemBonus({ bonusId });
        if (isBonusSliderCRMBonusClickable) {
          setLocalStorageValue(
            LOCAL_STORAGE_KEYS.BONUS_SLIDER_ACTIVE_BONUS,
            bonusId
          );
        }
        logAmplitude({
          actionName: LOG_ACTIONS.DEPOSIT_BUTTON_CLICK,
          attributes: {
            entry_point: 'bonuses',
            verified: !isNotVerified,
            deposit_limits_set: !isNeedSetupLimits,
            bonus_option: 'funds',
            bonus_template_id: bonusModelId
          },
          isMatchPath: true,
          additionalRoutes: {
            profile: profilePage
          }
        });
        push(deposit);
      } else if (isWagerAvailable) {
        await handleWagerAccept();
      } else if (isCanAccept) {
        await handleAccept();
      } else if (isBonusSystemProposed) {
        await handleStartPrewagerBonus();
      }
    },
    [
      isNoWallet,
      isShowWagerButton,
      isPotential,
      isBonusSystemPotential,
      isWagerAvailable,
      isCanAccept,
      isBonusSystemProposed,
      push,
      createWalletPage,
      isWagerAcceptCasino,
      casinoWagerLink,
      liveSportAll,
      isBonusSliderCRMBonusClickable,
      deposit,
      activeBonusSystemBonusId,
      bonusId,
      handleWagerAccept,
      handleAccept,
      handleStartPrewagerBonus
    ]
  );

  const openDetails = useCallback(() => {
    if (!isRequestProcessing) {
      push(`${fundsBonusDetails}/${isBsBonus ? 'bs' : ''}${bonusId}`);
    }
  }, [isRequestProcessing, push, fundsBonusDetails, isBsBonus, bonusId]);

  return {
    openDetails,
    acceptBonus,
    isRequestProcessing
  };
};
